import { useQueryParams } from '@hooks/queryParams';
import { useElementMeasures } from '@hooks/useElementMeasures';
import { appScrollTo, getAppScrollTop } from '@hooks/appScroll/helpers';
import { uuidRegEx } from '@utils/consts';
import { getElementStyle } from '@utils/getElementStyle';
import { useCallback, useEffect } from 'react';

export const useDashboardItems = () => {
  const headerHeight = useElementMeasures('header').height;
  const dashboardHeaderHeight = useElementMeasures('dashboard-header').height;
  const stickyHeaderHeight = useElementMeasures('sticky-header').height;
  const dashboardStickyHeaderHeight = useElementMeasures('dashboard-sticky-header').height;
  const gap = getElementStyle('dashboard-layout', 'gap');
  const mainPadding = getElementStyle('main', 'padding');
  const backButtonHeight = useElementMeasures('back-button').height;
  const isStickyHeader = document.querySelector('.dashboard-header')?.classList.contains('sticky');

  const {
    params: { focus, ...params },
    setParams,
  } = useQueryParams();

  const openedItems = (params.open || []).filter((el) => uuidRegEx.test(el));

  const isOpen = (id: string) => Boolean(openedItems.includes(id));

  const scrollToItem = useCallback(
    (id: string) => {
      const item = document.querySelector(`#dashboard-item-${id}`) as HTMLElement;

      const baseMargin = headerHeight + dashboardStickyHeaderHeight + stickyHeaderHeight + gap;

      const marginTop =
        baseMargin +
        (isStickyHeader ? dashboardHeaderHeight + mainPadding - 2 : 0) +
        (backButtonHeight ? backButtonHeight + gap : 0);

      appScrollTo({
        top: getAppScrollTop() + item?.getBoundingClientRect().top - marginTop,
        behavior: 'smooth',
      });
    },
    [
      headerHeight,
      dashboardStickyHeaderHeight,
      stickyHeaderHeight,
      gap,
      isStickyHeader,
      dashboardHeaderHeight,
      mainPadding,
      backButtonHeight,
    ],
  );

  const open = (id: string, withScroll?: boolean) => {
    if (!isOpen(id)) {
      const itemsWithNewIncluded = [...(openedItems || []), id];
      setParams(
        { open: itemsWithNewIncluded, focus: id },
        { preventScrollReset: true, disableSaveFilters: true },
      );
    }
    if (withScroll) scrollToItem(id);
  };

  const close = (id: string) => {
    const itemsWithCurrentExcluded = openedItems.filter((el) => el !== id);
    setParams(
      {
        open: itemsWithCurrentExcluded.length ? itemsWithCurrentExcluded : undefined,
        focus: undefined,
      },
      { preventScrollReset: true, disableSaveFilters: true },
    );
  };

  const toggle = (id: string) => {
    if (isOpen(id)) close(id);
    else open(id);
  };

  useEffect(() => {
    if (!focus) return;
    scrollToItem(focus);
  }, [focus, scrollToItem]);

  return { open, toggle, isOpen };
};
